import { BrowserModule } from '@angular/platform-browser';
import { DEFAULT_CURRENCY_CODE, NgModule, APP_INITIALIZER, ErrorHandler as AngularErrorHandler } from '@angular/core';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { HeaderComponent } from './components/header/header.component';
import { HomeComponent } from './pages/home/home.component';
import { VehiclesComponent } from './pages/calculations/vehicles/vehicles.component';
import { LogoutComponent } from './pages/logout/logout.component';
import { NotAuthorizedComponent } from './pages/not-authorized/not-authorized.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule, registerLocaleData } from '@angular/common';
import { AuthHttpInterceptor, AuthModule } from '@auth0/auth0-angular';
import { LoggedOutComponent } from './pages/logged-out/logged-out.component';
import { LayoutComponent } from './components/layout/layout.component';
import { GeneralComponent } from './pages/account/general/general.component';
import { BillingComponent } from './pages/account/billing/billing.component';
import { SecurityComponent } from './pages/account/security/security.component';
import { AccountHeaderComponent } from './components/account/header/account-header.component';
import { NotificationsComponent } from './pages/account/notifications/notifications.component';
import { OrganizationSettingsComponent } from './pages/organization/settings/organization-settings.component';
import { OrganizationMembersComponent } from './pages/organization/members/organization-members.component';
import { OrganizationHeaderComponent } from './components/organization/header/organization-header.component';
import { OrganizationBillingComponent } from './pages/organization/billing/organization-billing.component';
import { MandateCreateComponent } from './pages/mandates/create/mandate-create.component';
import { ClientListComponent } from './pages/clients/list/client-list.component';
import {
  SearchTableHeaderComponent,
} from './components/search-table/search-table-header/search-table-header.component';
import {
  SearchTableFooterComponent,
} from './components/search-table/search-table-footer/search-table-footer.component';
import { CreatePersonalComponent } from './pages/clients/create-personal/create-personal.component';
import { CreateCorporateComponent } from './pages/clients/create-corporate/create-corporate.component';
import { SidebarComponent } from './components/sidebar/sidebar.component';

import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { provideTranslateHttpLoader, TranslateHttpLoader } from '@ngx-translate/http-loader';
import { environment } from '../environments/environment';

import { OrganizationCreateComponent } from './pages/organization/create/organization-create.component';
import { OrganizationCreateFormFactory } from './pages/organization/create/organization-create-form.factory';
import { OrganizationApiController } from './api-controllers/organization.api-controller';
import { OrganizationUpdateFormFactory } from './pages/organization/settings/organization-update-form.factory';

import { TimelineComponent } from './components/timeline/timeline.component';
import { EventApiController } from './api-controllers/event.api-controller';
import { CURRENCY_MASK_CONFIG, CurrencyMaskConfig, CurrencyMaskModule } from 'ng2-currency-mask';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { CalculationsModule } from '../../../modules/src/lib/calculations/calculations.module';
import { CoreModule } from '../../../modules/src/lib/core/core.module';
import {
  TimelineCalculationRunEventComponent,
} from './components/timeline/timeline-calculation-run-event/timeline-calculation-run-event.component';
import { TimelineEventModelMapper } from './components/timeline/timeline-event.model-mapper';
import { EventStore } from './stores/event.store';
import { UserUpdateFormFactory } from './pages/account/general/user-update-form.factory';
import { CreatePersonalClientFormFactory } from './pages/clients/create-personal/create-personal-client-form.factory';
import {
  CreateCorporateClientFormFactory,
} from './pages/clients/create-corporate/create-corporate-client-form.factory';
import { RegisterComponent } from './pages/register/register.component';
import localeFrCa from '@angular/common/locales/fr-CA';
import { ServiceWorkerModule } from '@angular/service-worker';
import { RootComponent } from './components/root/root.component';
import { QuestionnairesModule } from '../../../modules/src/lib/questionnaires/questionnaires.module';
import {
  ListClientQuestionnairesComponent,
} from './pages/client-questionnaires/list/list-client-questionnaires.component';
import {
  CreateClientQuestionnaireComponent,
} from './pages/client-questionnaires/create/create-client-questionnaire.component';
import {
  CreateClientQuestionnaireFactory,
} from './pages/client-questionnaires/create/create-client-questionnaire.factory';
import {
  QuebecTaxForm2022ViewClientQuestionnaireComponent,
} from './pages/client-questionnaires/quebec-tax-form-2022/view/quebec-tax-form-2022-view-client-questionnaire.component';
import { AdvancedAuthModule } from '../../../modules/src/lib/advanced-auth/advanced-auth.module';
import {
  NotifyClientQuestionnaireComponent,
} from './pages/client-questionnaires/notify/notify-client-questionnaire.component';
import { ErrorComponent } from './pages/error/error.component';
import { OrganizationManager } from './managers/organization.manager';
import { LifecycleListenerInterfaceToken } from '../../../modules/src/lib/core/interfaces/lifecycle-listener.interface';
import { OrganizationLifecycleListener } from './listeners/organization-lifecycle.listener';
import { LicenseStatusBarComponent } from './components/license-status-bar/license-status-bar.component';
import { SubscriptionSuspendedComponent } from './pages/subscription/suspended/subscription-suspended.component';
import { SubscriptionCreateComponent } from './pages/subscription/create/subscription-create.component';
import { BillingApiController } from './api-controllers/billing.api-controller';
import { CreateSubscriptionFormFactory } from './pages/subscription/create/create-subscription-form.factory';
import { SubscriptionCreatedComponent } from './pages/subscription/created/subscription-created.component';
import { ListInvitesComponent } from './pages/invites/list/list-invites.component';
import { InviteApiController } from './api-controllers/invite.api-controller';
import { EmployeeApiController } from './api-controllers/employee.api-controller';
import { EmployeeInviteFormFactory } from './pages/employees/create/employee-invite-form.factory';
import { EmployeeInviteComponent } from './pages/employees/create/employee-invite.component';
import { EmployeeInviteListComponent } from './pages/employees/invite-list/employee-invite-list.component';
import { EmployeeListComponent } from './pages/employees/list/employee-list.component';
import { EmployeeSummaryComponent } from './pages/employees/summary/employee-summary.component';
import { BillingManager } from './managers/billing.manager';
import { AccessGuard } from './guards/access.guard';
import { HasOrganizationGuard } from './guards/has-organization.guard';
import { HasNoOrganizationGuard } from './guards/has-no-organization.guard';
import {
  DeletePaymentMethodModalComponent,
} from './components/delete-payment-method-modal/delete-payment-method-modal.component';
import { OnlyLoginGuard } from './guards/only-login.guard';
import {
  CreatePaymentMethodModalComponent,
} from './components/create-payment-method-modal/create-payment-method-modal.component';
import { SubscriptionUpgradeComponent } from './pages/subscription/upgrade/subscription-upgrade.component';
import { CreditCardRowComponent } from './components/credit-card-row/credit-card-row.component';
import { HasActivatedLicenseGuard } from './guards/has-activated-license.guard';
import { HasNoActivatedLicenseGuard } from './guards/has-no-activated-license.guard';
import {
  ClientQuestionnaireListTableComponent,
} from './pages/client-questionnaires/list-table/client-questionnaire-list-table.component';
import { StripeManager } from './managers/stripe.manager';
import { RightSidebarComponent } from './components/right-sidebar/right-sidebar.component';
import { InviteManager } from './managers/invite.manager';
import { MagienoFloatingButtonComponent } from '@magieno/angular-floating-button';
import {
  SalaryDividendViewClientQuestionnaireComponent,
} from './pages/client-questionnaires/salary-dividend/view/salary-dividend-view-client-questionnaire.component';
import { MagienoCoreModule } from '@magieno/angular-core';
import { MagienoAdvancedFormsModule } from '@magieno/angular-advanced-forms';
import { MagienoAdvancedTableComponent } from '@magieno/angular-advanced-table';
import { provideTranslateService } from '@ngx-translate/core';

registerLocaleData(localeFrCa);

export const CustomCurrencyMaskConfig: CurrencyMaskConfig = {
  align: 'right',
  allowNegative: true,
  decimal: '.',
  precision: 2,
  prefix: '',
  suffix: '',
  thousands: ' ',
};

@NgModule({
  declarations: [
    AppComponent,
    HeaderComponent,
    HomeComponent,
    VehiclesComponent,
    LogoutComponent,
    NotAuthorizedComponent,
    LoggedOutComponent,
    LayoutComponent,
    GeneralComponent,
    BillingComponent,
    SecurityComponent,
    AccountHeaderComponent,
    NotificationsComponent,
    OrganizationSettingsComponent,
    OrganizationMembersComponent,
    OrganizationHeaderComponent,
    OrganizationBillingComponent,
    OrganizationCreateComponent,
    MandateCreateComponent,
    // Client Questionnaires
    CreateClientQuestionnaireComponent,
    ListClientQuestionnairesComponent,
    ClientQuestionnaireListTableComponent,
    NotifyClientQuestionnaireComponent,
    QuebecTaxForm2022ViewClientQuestionnaireComponent,
    SalaryDividendViewClientQuestionnaireComponent,

    CreditCardRowComponent,
    // Employees
    EmployeeInviteComponent,
    EmployeeInviteListComponent,
    EmployeeListComponent,
    EmployeeSummaryComponent,
    // Invites
    ListInvitesComponent,
    // Modals
    CreatePaymentMethodModalComponent,
    DeletePaymentMethodModalComponent,
    ClientListComponent,
    SearchTableHeaderComponent,
    SearchTableFooterComponent,
    CreatePersonalComponent,
    CreateCorporateComponent,
    TimelineCalculationRunEventComponent,
    TimelineComponent,
    SidebarComponent,
    RightSidebarComponent,
    LicenseStatusBarComponent,
    RegisterComponent,
    SubscriptionCreateComponent,
    SubscriptionCreatedComponent,
    SubscriptionSuspendedComponent,
    SubscriptionUpgradeComponent,
    ErrorComponent,
    RootComponent,
  ],
  bootstrap: [AppComponent], imports: [CommonModule,
    BrowserModule,
    AppRoutingModule,
    NgbModule,
    FormsModule,
    // Optitax Modules
    AdvancedAuthModule,
    CoreModule,
    CalculationsModule,
    QuestionnairesModule,
    ReactiveFormsModule,
    CurrencyMaskModule,
    FontAwesomeModule,

    // Magieno
    MagienoFloatingButtonComponent,
    MagienoCoreModule,
    MagienoAdvancedFormsModule,
    MagienoAdvancedTableComponent,

    // Auth0

    AuthModule.forRoot({
      domain: window['optitax'].configuration.auth0.domain,
      clientId: window['optitax'].configuration.auth0.clientId,
      authorizationParams: {
        scope: 'openid profile email read:all',
        redirect_uri: window.location.origin + '/auth-callback',
      },
      httpInterceptor: {
        allowedList: [
          {
            uriMatcher: (uri: string) => {
              if (uri.includes('/api/public')) {
                return false;
              }
              return uri.includes('optitax.ca/api');
            },
            tokenOptions: {
              authorizationParams: {
                audience: environment.auth0Audience,
                scope: 'openid profile email read:all',
              },
            },
          }],
      },
      errorPath: '/auth-error',
      cacheLocation: 'localstorage',
    }),
    ServiceWorkerModule.register('ngsw-worker.js', {
      // For now, we are not going to activate PWAs since it seems that there's a bug in Angular 14.2. Also, the
      // cypress tests are failing when this is set to true.
      enabled: false,
      // Register the ServiceWorker as soon as the application is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000',
    })],
  exports: [
    HeaderComponent,
  ],
  providers: [
    // Factories
    CreateCorporateClientFormFactory,
    CreatePersonalClientFormFactory,
    CreateSubscriptionFormFactory,
    CreateClientQuestionnaireFactory,
    UserUpdateFormFactory,
    OrganizationCreateFormFactory,
    OrganizationUpdateFormFactory,
    OrganizationCreateFormFactory,
    EmployeeInviteFormFactory,
    // Forms
    // Api controllers
    EmployeeApiController,
    EventApiController,
    InviteApiController,
    OrganizationApiController,
    BillingApiController,
    // Guards
    AccessGuard,
    HasActivatedLicenseGuard,
    HasNoActivatedLicenseGuard,
    HasOrganizationGuard,
    HasNoOrganizationGuard,
    OnlyLoginGuard,
    // Managers
    BillingManager,
    InviteManager,
    OrganizationManager,
    StripeManager,
    // Mappers
    TimelineEventModelMapper,
    // Stores
    EventStore,
    // Configs
    { provide: LifecycleListenerInterfaceToken, useClass: OrganizationLifecycleListener, multi: true },
    // Parameters
    {
      provide: 'environment.api.url',
      useValue: environment.apiUrl,
    },
    {
      provide: 'environment.auth0.audience',
      useValue: environment.auth0Audience,
    },
    {
      provide: 'environment.is-debug',
      useValue: environment.isDebug,
    },
    {
      provide: 'environment.flags',
      useValue: environment.flags,
    },
    {
      provide: 'environment.name',
      useValue: environment.name,
    },
    {
      provide: 'environment.production',
      useValue: environment.production,
    },
    {
      provide: 'environment.logging.severityType',
      useValue: environment.logging.severityType,
    },
    {
      provide: 'magieno.observability.api_key',
      useValue: environment.magieno.observabilityApiKey,
    },
    { provide: HTTP_INTERCEPTORS, useClass: AuthHttpInterceptor, multi: true },
    { provide: DEFAULT_CURRENCY_CODE, useValue: 'CAD' },
    { provide: CURRENCY_MASK_CONFIG, useValue: CustomCurrencyMaskConfig },
    provideHttpClient(withInterceptorsFromDi()),
    provideTranslateService({
      loader: provideTranslateHttpLoader({
        prefix: '/assets/i18n/',
        suffix: '.json',
      }),
      fallbackLang: 'fr',
      lang: 'fr',
    }),
  ],
})
export class AppModule {
}